import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Activity,
  ArrowUpRight,
  CalendarClock,
  CreditCard,
  Gavel,
  Layers,
  Search,
  Sparkles,
  Zap,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Header from "./Header";
import SEO from "./SEO";
import "./AppStats.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const fade = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.28, ease: [0.32, 0.72, 0, 1] },
};

const nf = (n) =>
  typeof n === "number" && n >= 0 ? n.toLocaleString("en-US") : "—";

function daysUntil(dateish) {
  if (!dateish) return null;
  const d = new Date(dateish);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.ceil((d.getTime() - Date.now()) / 86400000));
}

export default function AppStats() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [sub, setSub] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [subFailed, setSubFailed] = useState(false);

  // Platform-wide counters. null = loading, -1 = unavailable.
  const [platform, setPlatform] = useState({
    debates: null,
    research: null,
    total: null,
    researchIsMock: false,
    live: false,
  });

  /* ---- personal usage (only meaningful when signed in) ---- */
  useEffect(() => {
    if (!isAuthenticated) {
      setSubLoading(false);
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        const resp = await axios.get(`${API_URL}/payments/subscription-status`, {
          withCredentials: true,
        });
        if (active) setSub(resp.data);
      } catch (err) {
        if (active) setSubFailed(true);
      } finally {
        if (active) setSubLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [isAuthenticated]);

  /* ---- platform counters: fetch, then keep live over SSE ---- */
  useEffect(() => {
    let active = true;
    let stream = null;

    const apply = (debates, research, isMock) => {
      if (!active) return;
      setPlatform((p) => ({
        ...p,
        debates,
        research,
        total: debates >= 0 && research >= 0 ? debates + research : -1,
        researchIsMock: !!isMock,
      }));
    };

    const fetchOnce = () => {
      Promise.all([
        fetch(`${API_URL}/stats/debates-completed`, { credentials: "omit" }),
        fetch(`${API_URL}/stats/research-analyses-completed`, {
          credentials: "omit",
        }),
      ])
        .then(async ([d, r]) => {
          if (!active) return;
          if (!d.ok || !r.ok) return apply(-1, -1, false);
          const [dj, rj] = await Promise.all([d.json(), r.json()]);
          apply(
            typeof dj?.count === "number" ? dj.count : -1,
            typeof rj?.count === "number" ? rj.count : -1,
            rj?.isMock,
          );
        })
        .catch(() => apply(-1, -1, false));
    };

    fetchOnce();

    try {
      stream = new EventSource(`${API_URL}/stats/live`);
      stream.addEventListener("stats", (event) => {
        if (!active) return;
        try {
          const d = JSON.parse(event.data);
          apply(
            typeof d?.debatesCompleted === "number" ? d.debatesCompleted : -1,
            typeof d?.researchAnalysesCompleted === "number"
              ? d.researchAnalysesCompleted
              : -1,
            d?.researchAnalysesIsMock,
          );
          setPlatform((p) => ({ ...p, live: true }));
        } catch {
          /* keep last known values */
        }
      });
      stream.onerror = () => {
        if (!active) return;
        setPlatform((p) => ({ ...p, live: false }));
        fetchOnce();
      };
    } catch {
      /* SSE unsupported — the one-shot fetch above still populated the values */
    }

    return () => {
      active = false;
      stream?.close();
    };
  }, []);

  /* ---- derived ---- */
  const isTrial = sub?.isTrialing;
  const used = isTrial
    ? (sub?.trialDetails?.trialAiActionsUsed ?? 0)
    : (sub?.aiActionsUsed ?? 0);
  const limit = sub?.aiMonthlyLimit ?? 0;
  const remaining = Math.max(0, limit - used);
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  const planLabel =
    sub?.subscriptionStatus !== "active" &&
    sub?.subscriptionStatus !== "trialing" &&
    (sub?.adminAllocatedActionsTotal || 0) > 0
      ? "Admin Invited"
      : sub?.subscriptionPlan && sub.subscriptionPlan !== "free"
        ? sub.subscriptionPlan.charAt(0).toUpperCase() +
          sub.subscriptionPlan.slice(1)
        : "Free";

  const statusTone =
    sub?.subscriptionStatus === "active"
      ? "success"
      : sub?.subscriptionStatus === "trialing"
        ? "accent"
        : sub?.subscriptionStatus === "past_due" ||
            sub?.subscriptionStatus === "unpaid"
          ? "danger"
          : "";

  const renewDate = isTrial
    ? sub?.trialDetails?.trialEnd || sub?.trialEnd
    : sub?.subscriptionEndDate;
  const daysLeft = daysUntil(renewDate);
  const ended = renewDate ? new Date(renewDate).getTime() < Date.now() : false;
  const winding =
    !!sub?.cancelAtPeriodEnd || sub?.subscriptionStatus === "canceled";

  const usageTone = pct >= 90 ? "danger" : pct >= 70 ? "warning" : "accent";

  const kpis = [
    {
      key: "used",
      label: isTrial ? "Trial actions used" : "AI actions used",
      value: subLoading ? null : `${nf(used)}`,
      sub: limit > 0 ? `of ${nf(limit)}` : "no limit set",
      Icon: Zap,
      tone: usageTone,
      bar: limit > 0 ? pct : null,
    },
    {
      key: "left",
      label: "Actions remaining",
      value: subLoading ? null : limit > 0 ? nf(remaining) : "—",
      sub: limit > 0 ? `${100 - pct}% of allowance` : "—",
      Icon: Activity,
      tone: "",
    },
    {
      key: "plan",
      label: "Current plan",
      value: subLoading ? null : planLabel,
      sub: sub?.subscriptionStatus
        ? isTrial
          ? "Free trial"
          : sub.subscriptionStatus.replace(/_/g, " ")
        : "inactive",
      Icon: CreditCard,
      tone: statusTone,
    },
    {
      key: "renew",
      label: isTrial
        ? "Trial ends in"
        : ended
          ? "Period ended"
          : winding
            ? "Access until"
            : "Renews in",
      value: subLoading
        ? null
        : renewDate === null || renewDate === undefined
          ? "—"
          : ended
            ? new Date(renewDate).toLocaleDateString()
            : `${daysLeft}`,
      sub: !renewDate
        ? "no active period"
        : ended
          ? "no longer active"
          : `${daysLeft === 1 ? "day" : "days"} · ${new Date(renewDate).toLocaleDateString()}`,
      Icon: CalendarClock,
      tone: ended ? "" : daysLeft !== null && daysLeft <= 3 ? "warning" : "",
    },
  ];

  return (
    <div className="stats-page">
      <SEO
        title="Overview"
        path="/dashboard"
        description="Your LawFusion usage and platform activity at a glance."
        noIndex={true}
      />
      <Header />

      <div className="stats-shell">
        <div className="stats-aurora" aria-hidden="true" />
        <div className="stats-container">
          {/* masthead */}
          <motion.header className="stats-masthead" {...fade}>
            <div>
              <p className="ds-eyebrow">Overview</p>
              <h1 className="stats-title">
                {user?.name ? `Welcome back, ${user.name}` : "Your workspace"}
              </h1>
              <p className="stats-subtitle">
                Usage on your account, and what the platform has run overall.
              </p>
            </div>
            <div className="stats-actions">
              <Link to="/research" className="ds-btn ds-btn-primary">
                <Search size={15} />
                New research
              </Link>
              <Link to="/debate" className="ds-btn ds-btn-secondary">
                <Gavel size={15} />
                Debate
              </Link>
            </div>
          </motion.header>

          {/* not signed in */}
          {!isAuthenticated && (
            <motion.div className="ds-card stats-signin" {...fade}>
              <Sparkles size={18} />
              <div>
                <strong>Sign in to see your usage.</strong>
                <span>
                  Platform activity below is live for everyone.
                </span>
              </div>
              <button
                type="button"
                className="ds-btn ds-btn-primary ds-btn-sm"
                onClick={() => navigate("/login")}
              >
                Sign in
              </button>
            </motion.div>
          )}

          {/* personal KPIs */}
          {isAuthenticated && (
            <section className="stats-section">
              <h2 className="stats-section-title">Your account</h2>

              {subFailed ? (
                <div className="ds-card stats-empty">
                  Couldn’t load your usage right now. Try refreshing.
                </div>
              ) : (
                <div className="stats-kpis ds-stagger">
                  {kpis.map(({ key, label, value, sub: s, Icon, tone, bar }) => (
                    <div key={key} className={`ds-card stats-kpi stats-tone-${tone || "none"}`}>
                      <div className="stats-kpi-head">
                        <span className="stats-kpi-icon">
                          <Icon size={15} />
                        </span>
                        <span className="stats-kpi-label">{label}</span>
                      </div>

                      {value === null ? (
                        <div className="ds-skeleton" style={{ height: 30, width: 90, marginTop: 4 }} />
                      ) : (
                        <p className="stats-kpi-value">{value}</p>
                      )}

                      <p className="stats-kpi-sub">{s}</p>

                      {typeof bar === "number" && (
                        <div className="ds-progress stats-kpi-bar">
                          <motion.div
                            className="ds-progress-bar"
                            initial={{ width: 0 }}
                            animate={{ width: `${bar}%` }}
                            transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* platform activity */}
          <section className="stats-section">
            <h2 className="stats-section-title">
              Platform activity
              {platform.live && (
                <span className="ds-chip ds-chip-accent stats-live">
                  <span className="ds-dot ds-dot-live" />
                  Live
                </span>
              )}
            </h2>

            <div className="stats-platform ds-stagger">
              {[
                {
                  key: "research",
                  label: "Research analyses run",
                  value: platform.research,
                  Icon: Search,
                  note: platform.researchIsMock ? "includes seeded baseline" : null,
                },
                {
                  key: "debates",
                  label: "Debates completed",
                  value: platform.debates,
                  Icon: Gavel,
                },
                {
                  key: "total",
                  label: "Total analyses",
                  value: platform.total,
                  Icon: Layers,
                },
              ].map(({ key, label, value, Icon, note }) => (
                <div key={key} className="ds-card stats-platform-card">
                  <span className="stats-platform-icon">
                    <Icon size={17} />
                  </span>
                  {value === null ? (
                    <div className="ds-skeleton" style={{ height: 32, width: 110 }} />
                  ) : (
                    <p className="stats-platform-value">{nf(value)}</p>
                  )}
                  <p className="stats-platform-label">{label}</p>
                  {note && <p className="stats-platform-note">{note}</p>}
                </div>
              ))}
            </div>
          </section>

          {/* shortcuts */}
          <section className="stats-section">
            <h2 className="stats-section-title">Jump back in</h2>
            <div className="stats-links ds-stagger">
              {[
                { to: "/research", Icon: Search, t: "Research mode", d: "Run one issue across multiple models" },
                { to: "/debate", Icon: Gavel, t: "Debate mode", d: "Adversarial multi-agent reasoning" },
                { to: "/settings", Icon: CreditCard, t: "Settings & billing", d: "Profile, appearance, plan" },
              ].map(({ to, Icon, t, d }) => (
                <Link key={to} to={to} className="ds-card ds-card-interactive stats-link">
                  <span className="stats-link-icon">
                    <Icon size={16} />
                  </span>
                  <span className="stats-link-text">
                    <span className="stats-link-title">{t}</span>
                    <span className="stats-link-desc">{d}</span>
                  </span>
                  <ArrowUpRight size={15} className="stats-link-arrow" />
                </Link>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
